import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchSkuGroups, fetchSkuItemsForSubgroups, fetchSkuSubgroups } from './SkuItems.helpers';
import { getMenuId, toComparableId } from './SkuMenuData.normalizers';
import { getDefaultSelectedMenuId } from './SkuMenuOverview.helpers';
import { skuMenuKeys } from './skuMenuKeys';

export const SKU_ITEMS_TAB = 'items';

export const getStorageKey = venueId => `sku-selected-menu-${venueId}`;

const readPersistedMenuId = venueId => {
  try {
    return sessionStorage.getItem(getStorageKey(venueId)) || null;
  } catch {
    return null;
  }
};

const persistMenuId = (venueId, menuId) => {
  try {
    if (menuId) {
      sessionStorage.setItem(getStorageKey(venueId), menuId);
    } else {
      sessionStorage.removeItem(getStorageKey(venueId));
    }
  } catch {
    // sessionStorage unavailable
  }
};

// Watch the error reference (not isError boolean) so consecutive failures each notify.
// React Query produces a fresh Error instance per failed fetch, so the effect re-runs.
const useNotifyOnQueryError = (error, onError) => {
  useEffect(() => {
    if (error) onError();
  }, [error, onError]);
};

export default function useSkuMenuState({ venueId, onMenuLoadError, onItemsLoadError }) {
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(SKU_ITEMS_TAB);
  const [selectedMenuId, setSelectedMenuIdState] = useState(() => {
    const persisted = readPersistedMenuId(venueId);
    return persisted ? toComparableId(persisted) : null;
  });
  const setSelectedMenuId = nextMenuId => {
    const normalized = toComparableId(nextMenuId);
    setSelectedMenuIdState(normalized);
    persistMenuId(venueId, normalized);
  };

  const menusQuery = useQuery({
    queryKey: skuMenuKeys.menus(venueId),
    queryFn: () => fetchSkuGroups(venueId),
    enabled: !!venueId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const detailsQuery = useQuery({
    queryKey: skuMenuKeys.details(venueId),
    queryFn: async () => {
      const categories = await fetchSkuSubgroups(venueId);
      const items = await fetchSkuItemsForSubgroups(venueId, categories);
      return { categories, items };
    },
    enabled: !!venueId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useNotifyOnQueryError(menusQuery.error, onMenuLoadError);
  useNotifyOnQueryError(detailsQuery.error, onItemsLoadError);

  const menus = menusQuery.data ?? [];
  const categories = detailsQuery.data?.categories ?? [];
  const skuItems = detailsQuery.data?.items ?? [];

  useEffect(() => {
    if (!menusQuery.data) {
      return;
    }
    setSelectedMenuIdState(previousSelectedMenuId => {
      const normalizedPreviousMenuId = toComparableId(previousSelectedMenuId);
      const hasPreviousMenu = menusQuery.data.some(menu => getMenuId(menu) === normalizedPreviousMenuId);

      if (normalizedPreviousMenuId && hasPreviousMenu) {
        return normalizedPreviousMenuId;
      }

      const persisted = readPersistedMenuId(venueId);
      const normalizedPersisted = toComparableId(persisted);
      const hasPersistedMenu =
        normalizedPersisted && menusQuery.data.some(menu => getMenuId(menu) === normalizedPersisted);

      const nextMenuId = hasPersistedMenu ? normalizedPersisted : getDefaultSelectedMenuId(menusQuery.data);
      persistMenuId(venueId, nextMenuId);
      return nextMenuId;
    });
  }, [menusQuery.data]);

  const refetch = useCallback(() => queryClient.invalidateQueries({ queryKey: skuMenuKeys.all }), [queryClient]);

  return {
    selectedTab,
    setSelectedTab,
    menus,
    selectedMenuId,
    setSelectedMenuId,
    categories,
    skuItems,
    isLoading: menusQuery.isLoading || detailsQuery.isLoading,
    refreshTimestampKey: detailsQuery.dataUpdatedAt,
    refetch,
  };
}
